import {
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useElements,
    useListen,
    useProp,
} from '@atomify/hooks';
import { INVIEW_CLASS, InviewContainerElement } from '@source/components/atoms/inview-container';
export interface Slide {
    left: number;
}

const SLIDER_CONTROL_ACTIVE = 'slider__control--is-active';
const ITEM_INVIEW_STATE = INVIEW_CLASS;
const CLASS_NO_TRANSITION = 'slider__list--no-transition';
const SLIDER_HAS_PREVIOUS_SLIDE = 'slider--has-previous-slide';
const SLIDER_HAS_NEXT_SLIDE = 'slider--has-next-slide';

export interface SliderInterface extends HTMLElement {
    nextSlide: Function;
    previousSlide: Function;
    slideToItem: Function;
    isLastSlide: () => boolean;
    currentSlide: number;
}

const Slider: FC<SliderInterface> = ({ element }) => {
    const [start] = useProp<number | null>('start', null);
    const [controlId] = useProp<string | null>('controlId', null);

    const list = useElement<HTMLDivElement>('[js-hook-slider-list]');
    const inviewContainer = useElement<InviewContainerElement>('inview-container');
    const items = useElements<(HTMLDivElement | null)[]>('[js-hook-slider-item]');

    const previousSelector = controlId
        ? `[js-hook-slider-control-previous=${controlId}]`
        : '[js-hook-slider-control-previous]';

    const nextSelector = controlId
        ? `[js-hook-slider-control-next=${controlId}]`
        : '[js-hook-slider-control-next]';

    const controlOptions = controlId ? { root: document } : undefined;

    const controlPrevious = useElement<HTMLButtonElement | null>(previousSelector, controlOptions);
    const controlNext = useElement<HTMLButtonElement | null>(nextSelector, controlOptions);

    const slides: Slide[] = [];
    let currentPosition: number = 0;
    let currentSlideIndex: number = 0;

    useListen(controlNext, 'click', nextSlide);
    useListen(controlPrevious, 'click', previousSlide);
    useListen(inviewContainer, 'change', setControlsActiveState);

    useBindMethod('slideToItem', slideToItem);
    useBindMethod('nextSlide', nextSlide);
    useBindMethod('previousSlide', previousSlide);
    useBindMethod('isLastSlide', isLastSlide);

    onDidLoad(() => {
        initItemPositions();
    });

    /**
     * Saves the current positions of the slides
     *
     * @memberof Slider
     */
    function initItemPositions() {
        requestAnimationFrame(() => {
            Array.from(items.current).forEach((item) => {
                if (item) {
                    slides.push({
                        left: item.getBoundingClientRect().left,
                    });
                }
            });

            if (start) slideToItem(start, false);
        });
    }

    /**
     * Checks if the first or last element is in or out of view.
     * @returns { object }
     * @memberof Slider
     */
    function getInviewItems() {
        const filteredItems = getItemsInDOM();

        const isFirstCardInView =
            filteredItems.length > 0
                ? filteredItems[0].classList.contains(ITEM_INVIEW_STATE)
                : true;

        const isLastCardInView =
            filteredItems.length > 0
                ? filteredItems[filteredItems.length - 1].classList.contains(ITEM_INVIEW_STATE)
                : true;

        return {
            isFirstCardInView,
            isLastCardInView,
        };
    }

    /**
     * Set controls active state based upon the items that are in or out of view.
     * @public
     * @memberof Slider
     */
    function setControlsActiveState() {
        if (!controlPrevious.current) return;
        if (!controlNext.current) return;

        const { isFirstCardInView, isLastCardInView } = getInviewItems();

        // Apply active state to the previous control when the first card is out of view
        // and when the the active class is not applied.
        if (!isFirstCardInView) {
            controlPrevious.current.classList.add(SLIDER_CONTROL_ACTIVE);
            element.classList.add(SLIDER_HAS_PREVIOUS_SLIDE);
        } else if (isFirstCardInView) {
            controlPrevious.current.classList.remove(SLIDER_CONTROL_ACTIVE);
            element.classList.remove(SLIDER_HAS_PREVIOUS_SLIDE);
        }

        // Apply active state to the next control when the last card is out of view
        // and when the the active class is not applied.
        if (!isLastCardInView) {
            controlNext.current!.classList.add(SLIDER_CONTROL_ACTIVE);
            element.classList.add(SLIDER_HAS_NEXT_SLIDE);
        } else if (isLastCardInView) {
            controlNext.current.classList.remove(SLIDER_CONTROL_ACTIVE);
            element.classList.remove(SLIDER_HAS_NEXT_SLIDE);
        }
    }

    /**
     * Slides to the current slide based upon the direction.
     * @param { string } direction
     * @param { boolean } transition
     */
    function slideToItem(index: number, transition: boolean = true) {
        // Get current slide based upon card as key
        const nextSlide = slides[index];
        const currentSlide = slides[currentSlideIndex];

        if (!nextSlide) return;

        if (!transition) list.current!.classList.add(CLASS_NO_TRANSITION);

        currentPosition += nextSlide.left - currentSlide.left;

        list.current!.style.transform = `translateX(-${currentPosition}px)`;

        currentSlideIndex = index;

        if (!transition) {
            list.current!.offsetHeight;
            list.current!.classList.remove(CLASS_NO_TRANSITION);
        }
    }

    /**
     * Slides to the next slide, if available
     */
    function nextSlide() {
        if (!isLastSlide()) {
            const nextSlide = currentSlideIndex + 1;

            slideToItem(nextSlide);
        }
    }

    /**
     * Slides to the previous slide, if available
     */
    function previousSlide() {
        const previousSlide = currentSlideIndex - 1;

        slideToItem(previousSlide);
    }

    /**
     * Returns a boolean to check if current slide is the last one
     */
    function isLastSlide() {
        const { isLastCardInView } = getInviewItems();

        return isLastCardInView;
    }

    /**
     * @description Returns all items that are still in the DOM
     * @return {*}  {HTMLDivElement[]}
     */
    function getItemsInDOM(): HTMLDivElement[] {
        const itemsInDOM = Array.from(items.current).filter(
            (item) => !!item && item.offsetParent,
        ) as HTMLDivElement[];

        return itemsInDOM;
    }
};

Slider.props = {
    start: {
        type: Number,
        required: false,
    },
    controlId: {
        type: String,
        required: false,
    },
};

defineElement('bpd-slider', Slider);
